import React, {useMemo} from 'react';
import {
  Modal,
  Button,
  Headline,
  Body,
  designSystemToken,
  Label,
} from '@lightricks/react-design-system';
import {formatMessageBody} from '@/utils/formatMessageBody';
import translate from '@/utils/translate';
import styles from './MessageCreatorsModal.module.scss';
import {MESSAGE_MODAL_SX} from './constants';

function FormattedBody({body}: {body: string}) {
  const html = useMemo(() => formatMessageBody(body), [body]);
  // eslint-disable-next-line react/no-danger
  return (
    <div
      className={styles.previewBody}
      dangerouslySetInnerHTML={{__html: html}}
    />
  );
}

interface MessageCreatorsPreviewModalProps {
  open: boolean;
  creatorCount: number;
  subject: string;
  body: string;
  onGoBack: () => void;
  onConfirm: () => void;
  isSending?: boolean;
}

function MessageCreatorsPreviewModal({
  open,
  creatorCount,
  subject,
  body,
  onGoBack,
  onConfirm,
  isSending = false,
}: MessageCreatorsPreviewModalProps) {
  return (
    <Modal
      open={open}
      handleClose={isSending ? undefined : onGoBack}
      showCloseButton
      size="large"
      sx={MESSAGE_MODAL_SX}
    >
      <div className={styles.previewContent}>
        <Headline size="md">
          {translate('Send to {count} creators', {count: creatorCount})}
        </Headline>
        <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
          {translate(
            "You're about to message {count} creators via email and in-app. Want to go ahead?",
            {count: creatorCount}
          )}
        </Body>
        <div className={styles.previewCard}>
          <Label
            size="sm"
            color={designSystemToken('semantic.fg.inverse-secondary')}
          >
            {translate('Subject')}
          </Label>
          <Headline size="xxs" color={designSystemToken('semantic.fg.primary')}>
            {subject}
          </Headline>
          <Label
            size="sm"
            color={designSystemToken('semantic.fg.inverse-secondary')}
          >
            {translate('Message')}
          </Label>
          <FormattedBody body={body} />
        </div>
        <div className={styles.previewFooter}>
          <Button
            mode="tinted"
            appearance="neutral"
            size="large"
            onClick={onGoBack}
            disabled={isSending}
          >
            {translate('Go back')}
          </Button>
          <Button
            mode="filled"
            appearance="neutral"
            size="large"
            onClick={onConfirm}
            disabled={isSending}
            isLoading={isSending}
          >
            {translate('Yes, send to {count} creators', {count: creatorCount})}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default MessageCreatorsPreviewModal;
